import { type PropsWithChildren, createContext, useContext } from 'react';

import type {
  Brand,
  Country,
  Language,
  Locale,
  Product,
  SiteName,
} from '@seek/melways-sites';
import type { Environment, Zone, Experience } from '..';

export interface BaseConfig {
  brand: Brand;
  country: Country;
  environment: Environment;
  experience: Experience;
  language: Language;
  locale: Locale;
  name: string;
  product: Product;
  siteName: SiteName;
  zone: Zone;
  clientIp?: string;
  consolidated?: boolean;
  requestId?: string;
  userAgent?: string;
}

type ConfigContextProps<T extends BaseConfig> =
  | {
      config: T;
    }
  | undefined;

type ConfigProviderProps<T extends BaseConfig> = PropsWithChildren<
  ConfigContextProps<T>
>;

const ConfigProviderContext =
  createContext<ConfigContextProps<BaseConfig>>(undefined);

export const ConfigProvider = <T extends BaseConfig>({
  children,
  config,
}: ConfigProviderProps<T>) => (
  <ConfigProviderContext.Provider value={{ config }}>
    {children}
  </ConfigProviderContext.Provider>
);

export const useConfig = <T extends BaseConfig>(): T => {
  const context = useContext(ConfigProviderContext);

  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }

  return { ...context.config } as unknown as T;
};
